import { format, isToday, isYesterday } from 'date-fns';
import type { ItemState } from './shared';

/**
 * Forms in the app (CmsForms.tsx): the shapes the API returns and the pure
 * helpers the Forms view uses — inbox dates, where a form is, CSV export.
 */

export interface FormRow {
  id: string;
  name: string;
  workingHash: string;
  fieldCount: number;
  state: ItemState;
  placedOn: Array<{ id: string; path: string; title: string }>;
  /** This person gets "N new responses" emails for it (never the content). */
  emailMe?: boolean;
  /** contact (readers write in; you reply) | signup (readers ask to be sent something). */
  purpose?: 'contact' | 'signup';
  /** Pages whose live or scheduled version shows it: what blocks a delete (app reads only). */
  livePlacedOn?: Array<{ id: string; path: string; title: string }>;
  responses: { total: number; unread: number; latestAt: string | null };
  updatedAt?: string;
  /** The template whose Look and pages added it ("Magazine"), not an agent (app reads only). */
  addedByTemplate?: string | null;
  /** An agent's create_form made it (the meta line's "Agent"). */
  madeByAgent?: boolean;
}

export interface FormField {
  name: string;
  label: string;
  type: string;
  required?: boolean;
  options?: string[];
  placeholder?: string;
  /** A line under the field. */
  help?: string;
}

export interface ResponseRow {
  id: string;
  receivedAt: string;
  read: boolean;
  page: string | null;
  /** 'chat': filled in through the reader chat, a question at a time. */
  via?: string | null;
  fields: Array<{ name: string; label: string; value: string }>;
}

/** "Today 1:58 PM", "Yesterday", "Oct 8" — how the inbox reads. */
export function receivedLabel(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (isToday(d)) return `Today ${format(d, 'h:mm a')}`;
  if (isYesterday(d)) return 'Yesterday';
  return d.getFullYear() === now.getFullYear() ? format(d, 'MMM d') : format(d, 'MMM d, yyyy');
}

/** Where a form is: "On About", "On Home and 2 more", or not on a page. */
export function placedLabel(placed: FormRow['placedOn']): string {
  if (!placed.length) return 'Not on a page yet';
  return placed.length === 1
    ? `On ${placed[0].title}`
    : `On ${placed[0].title} and ${placed.length - 1} more`;
}

/** One CSV cell: quoted when it holds a comma, quote or line break; formula-looking text defused. */
const csvCell = (v: unknown) => {
  let s = String(v ?? '');
  // A spreadsheet runs a cell that starts with = + - @ as a formula; readers wrote these.
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * Responses as CSV: Received, Page, then one column per field — the form's
 * fields in its order, then any older fields only earlier responses have.
 */
export function responsesCsv(fields: FormField[], rows: ResponseRow[]): string {
  const cols: Array<{ name: string; label: string }> = fields.map((f) => ({
    name: f.name,
    label: f.label,
  }));
  for (const r of rows) {
    for (const v of r.fields) {
      if (!cols.some((c) => c.name === v.name)) cols.push({ name: v.name, label: v.label });
    }
  }
  const head = ['Received', 'Page', ...cols.map((c) => c.label)];
  const lines = rows.map((r) => {
    const byName = new Map(r.fields.map((v) => [v.name, v.value]));
    return [
      new Date(r.receivedAt).toISOString(),
      r.page || '',
      ...cols.map((c) => byName.get(c.name) ?? ''),
    ]
      .map(csvCell)
      .join(',');
  });
  return [head.map(csvCell).join(','), ...lines].join('\r\n') + '\r\n';
}
